// ============================================================================
// usePreLitConfig — Pre-Lit policy config loader
// ============================================================================
// Surfaces the two Pre-Lit-modal config keys as primitives, falling back to
// the documented defaults if the Effective Config endpoint is unreachable.
//
//   prelit_action_max_backdate_days   (BACKDATING_POLICY group, default 7)
//   prelit_commitment_window_days     (LITIGATION_POLICY group, default 90)

import { useEffect, useState } from 'react';
import { configService } from 'api/services/configService';
import type { ConfigScopeRef } from 'api/types/config.types';

const DEFAULTS = {
  maxBackdateDays: 7,
  commitmentWindowDays: 90,
};

export interface PreLitConfig {
  maxBackdateDays: number;
  commitmentWindowDays: number;
  loaded: boolean;
}

/**
 * @param scopes  Optional PRODUCT scope so per-product overrides apply
 *                (e.g. PL-product = 3 days backdate). v1 callers pass none —
 *                Phase 8 adds the product context.
 */
export const usePreLitConfig = (scopes?: ConfigScopeRef[]): PreLitConfig => {
  const [maxBackdateDays, setMaxBackdateDays] = useState(DEFAULTS.maxBackdateDays);
  const [commitmentWindowDays, setCommitmentWindowDays] = useState(DEFAULTS.commitmentWindowDays);
  const [loaded, setLoaded] = useState(false);

  const scopesKey = JSON.stringify(scopes ?? []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch both groups in parallel; resolve the integer values per key.
        const [backdating, litigation] = await Promise.all([
          configService.getEffective('BACKDATING_POLICY', scopes).catch(() => ({ data: [] as any })),
          configService.getEffective('LITIGATION_POLICY', scopes).catch(() => ({ data: [] as any })),
        ]);
        if (cancelled) return;

        const backdate = (backdating.data ?? []).find((v: any) => v.configKey === 'prelit_action_max_backdate_days');
        const window_  = (litigation.data ?? []).find((v: any) => v.configKey === 'prelit_commitment_window_days');

        const parseInt10 = (s: string | null | undefined, fallback: number) => {
          if (!s) return fallback;
          const n = parseInt(s, 10);
          return Number.isFinite(n) && n >= 0 ? n : fallback;
        };

        setMaxBackdateDays(parseInt10(backdate?.effectiveValue ?? backdate?.defaultValue, DEFAULTS.maxBackdateDays));
        setCommitmentWindowDays(parseInt10(window_?.effectiveValue ?? window_?.defaultValue, DEFAULTS.commitmentWindowDays));
      } catch {
        // Hard fall-through to defaults — already initialised.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopesKey]);

  return { maxBackdateDays, commitmentWindowDays, loaded };
};
